import { useSyncExternalStore } from 'react';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { RoiStatistics } from '~/engine/util/algorithms/sdf/ContourStatistics';
import { useDomainStore } from '~/hooks/domain/useDomainStore';

/**
 * Reactively reads the SDF-derived shape statistics (surface area, sphericity,
 * center of mass, bounding box) for an ROI.
 *
 * Subscribes to the domain store — the same seam {@link useRoiDetailView} uses
 * for volume — so the panel refreshes whenever a contour edit bumps the store
 * version (a brush stroke commits via the contour service → `markChanged`).
 * The engine self-invalidates the stats cache on SDF data-version, so the
 * re-read returns fresh values; an unchanged ROI returns the same cached
 * object, so unrelated domain changes don't churn the panel.
 *
 * Returns undefined for point ROIs, empty ROIs, before the environment is
 * ready, or without a `roiNumber`, which computes nothing.
 */
export function useRoiStatistics(
    roiNumber: number | undefined
): RoiStatistics | undefined {
    // Resolved through the edit target: the grid Environment that shows the
    // active set is the one that receives the strokes, so its statistics are
    // the ones the clinician sees.
    const { environment: env } = useActiveEditTarget();
    const store = useDomainStore();
    return useSyncExternalStore(store.subscribe, () =>
        roiNumber === undefined ? undefined : env?.getRoiStatistics(roiNumber)
    );
}
